/**
 * @prop --placeholder-image-color: color of placeholder image (svg)
 */
:host {
  --service-background-color: #{$colorSecondary30};
  --service-color: #{$colorPrimary70};
  --placeholder-image-color: #{$colorGrey30};
  --hover-background-color: var(
    --stzh-card-superteaser-hover-background-color,
    #{$colorSecondary10}
  );

  --grid-template-areas: "heading" "description" "meta";
  --grid-template-rows: repeat(2, auto) minmax(0, 1fr);
  --grid-template-columns: minmax(0, 1fr);

  --tag-color: var(--stzh-card-superteaser-tag-color);
  --tag-color-hover: var(
    --stzh-card-superteaser-tag-color-hover,
    #{$colorSecondary60}
  );
  --tag-background-color: var(--stzh-card-superteaser-tag-background-color);

  --heading-link-color: var(
    --stzh-card-superteaser-heading-link-color,
    #{$colorPrimary70}
  );

  --slotted-heading-margin-bottom: var(
    --stzh-card-superteaser-slotted-heading-margin-bottom
  );
  --slotted-heading-color: var(--stzh-card-superteaser-slotted-heading-color);
  --slotted-description-color: var(
    --stzh-card-superteaser-slotted-description-color
  );

  display: grid;

  ::slotted(img) {
    width: 100%;
    max-width: 100%;
  }

  &[has-image] {
    --grid-template-areas: "image" "heading" "description" "meta";
    --grid-template-rows: repeat(3, auto) minmax(0, 1fr);
    --grid-template-columns: minmax(0, 1fr);
  }

  &[has-image][image-position="right"] {
    --grid-template-areas: "heading heading" "description description"
      "meta image";
    --grid-template-rows: repeat(2, auto) minmax(0, 1fr);
    --grid-template-columns: minmax(0, 1fr)
      minmax(calc(100px + #{space("medium")}), min-content);
  }
}

.stzh-card-superteaser {
  @include fontSize("milli");
  position: relative;
  display: grid;
  background-color: $colorGrey10;
  grid-template-areas: var(--grid-template-areas);
  grid-template-rows: var(--grid-template-rows);
  grid-template-columns: var(--grid-template-columns);
  color: $colorGrey80;

  &__tag,
  &__dateline,
  &__breadcrumb,
  &__event-infos,
  &__heading-link-text,
  &__description,
  &__meta-wrapper {
    z-index: 1;
    position: relative;
  }

  &__heading-wrapper,
  &--has-description-wrapper &__description-wrapper,
  &__meta-wrapper {
    margin-left: space("medium");
    margin-right: space("medium");
    margin-bottom: space("large");
  }

  &__heading-wrapper {
    grid-area: heading;
    display: grid;
    margin-top: space("xlarge");
  }

  &__image,
  &__meta-wrapper {
    align-self: end;
  }

  &__image {
    display: grid;
    grid-area: image;
    width: 100%;
  }

  &__image-placeholder {
    /* Apply color since the placeholder svg has fill "currentColor" */
    color: var(--placeholder-image-color);
  }

  &__description-wrapper {
    grid-area: description;
  }

  &__meta-wrapper {
    grid-area: meta;
  }

  &__meta {
    overflow: hidden;
  }

  &__download {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: space("xsmall");
    overflow: hidden;
  }

  &__download-button {
    align-self: flex-end;
    display: flex;
    justify-content: center;
    align-items: center;
    width: $formInputHeightSmall;
    height: $formInputHeightSmall;
    color: $colorPrimary70;
  }

  // &__download-heading {
  //   color: $colorPrimary70;
  //   text-underline-offset: $linkTextUnderlineOffset;
  //   text-decoration-line: $linkTextDecorationLine;
  //   text-decoration-thickness: $linkTextDecorationThickness;
  //   text-decoration-skip-ink: $linkTextDecorationSkipInk;
  // }

  &__tag,
  &__topic,
  &__service,
  &__event-date-teaser {
    z-index: 2;
  }

  &__tag {
    --stzh-tag-color: var(--tag-color);
    --stzh-tag-background-color: var(--tag-background-color);
    position: absolute;
    left: 0;
    top: 0;
  }

  &__service {
    @include font("title");
    @include fontSize("micro");
    position: absolute;
    top: 0;
    right: 0;
    background-color: var(--service-background-color);
    color: var(--service-color);
    padding: space("xsmall") space("medium");
    text-align: center;
  }

  &__topic {
    @include font("title");
    @include fontSize("micro");
    position: relative;
    color: $colorPrimary70;
    margin-bottom: space("xsmall");
  }

  &__dateline {
    @include fontSize("micro");
    color: $colorBlack;
    margin-bottom: space("xxsmall");
  }

  &__heading {
    margin-top: 0;
    margin-bottom: var(--stzh-space-small);
  }

  &__heading--slotted {
    --stzh-heading-color: var(--slotted-heading-color);
    margin-bottom: var(--slotted-heading-margin-bottom);
  }

  &__description--slotted {
    --stzh-heading-color: var(--slotted-description-color);
  }

  &__heading-link {
    @include font("title");
    @include fontCurve("teaser");
    display: block;
    color: var(--heading-link-color);
    text-decoration-line: none;
  }

  &__event-infos {
    @include fontSize("micro");
  }

  &:has(#{&}__description:not(:empty)) &__event-infos {
    margin-bottom: space("small");
  }

  &__description {
    &:empty {
      display: none;
    }
  }

  &__breadcrumb {
    margin-bottom: space("small");
  }

  &__event-date-teaser {
    @include fontSize("nano");
    text-transform: uppercase;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 88px;
    height: 88px;
    color: $colorBlack;
    background-color: $colorSecondary30;
    position: absolute;
    top: 0;
    right: 0;
  }

  &__event-date-teaser-day {
    @include font("title");
    @include fontSize("hecto");
    color: $colorPrimary70;
    margin-top: -7px;
    margin-bottom: -7px;
  }

  &__meta-list,
  &__download-meta-list {
    @include fontSize("micro");
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    column-gap: space("xsmall");
    margin-left: calc((#{space("xsmall")} + 1px) / -1);
  }

  &__meta-list-item,
  &__download-meta-list-item {
    display: flex;
    align-items: center;
    min-height: 24px;
    gap: space("xsmall");

    &::before {
      content: "";
      display: block;
      width: 1px;
      height: 16px;
      background-color: currentColor;
    }
  }

  /* Has link */

  &--has-link &__heading-link {
    border-radius: $buttonBorderRadius;
    cursor: pointer;

    &::before,
    &::after {
      content: "";
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      transition: background-color $baseTransitionAnimationSpeed;
    }

    &::after {
      z-index: 10;
    }
  }

  &--has-link:has(#{&}__heading-link:hover) &__heading-link {
    &::before {
      background-color: var(--hover-background-color);
    }
  }

  &--has-link:has(#{&}__heading-link:hover) &__tag {
    --color: var(--tag-color-hover);
  }

  /* Has tag */

  &--has-tag &__heading-wrapper {
    margin-top: space("xxxlarge");
  }

  /* Has service */

  &--has-service &__heading-wrapper {
    margin-top: space("xxxxlarge");
  }

  &--has-service#{&}--has-image-right &__heading-wrapper {
    margin-top: space("xxxlarge");
  }

  /* Has image (default) */

  &--has-image &__image {
    z-index: 1;
    position: relative;
  }

  &--has-image-default &__heading-wrapper {
    margin-top: space("medium");
  }

  /* Has image right */

  &--has-image-right &__image {
    margin-bottom: space("large");
    padding-right: space("medium");
  }

  &--has-image-right &__meta-wrapper {
    margin-right: space("xsmall");
  }

  &--has-image-right &__service {
    position: static;
  }

  /* Has description wrapper, meta or download */

  &--has-description-wrapper &__heading-wrapper,
  &--has-meta &__heading-wrapper,
  &--has-download &__heading-wrapper {
    margin-bottom: 0px;
  }

  &--has-meta &__description-wrapper,
  &--has-download &__description-wrapper {
    margin-bottom: 0px;
  }

  &--has-meta &__description,
  &--has-download &__description {
    &:not(:empty) {
      margin-bottom: space("small");
    }
  }

  &--has-download &__meta {
    margin-bottom: space("xsmall");
  }

  /* Hyphens */
  &--hyphens &__heading {
    hyphens: auto;
  }

  /* Is on Homepage */
  &--is-on-homepage#{&}--is-event &__event-date-teaser,
  &--is-on-homepage#{&}--is-event ::slotted([slot="image"]),
  &--is-on-homepage#{&}--is-event ::slotted(stzh-ratio) {
    display: none;
  }

  &--is-on-homepage#{&}--is-event &__heading-wrapper {
    margin-top: space("xxxlarge");
  }

  &--is-on-homepage#{&}--is-event &__event-date {
    @include font("title");
    color: var(--stzh-color-primary);
    margin-bottom: space("xsmall");
  }

  &--is-on-homepage#{&}--is-event &__event-date span {
    display: block;
  }
}
